/* ==========================================================================
   Rook UI Core — Sample Drawer Controller
   Web Component: <rk-sample-drawer-element>

   Coordena três peças renderizadas no drawer (blocks/rk-sample-drawer.liquid):
   - Lista de amostras selecionadas (snippets/rk-sample-card.liquid), com botão
     de remover — `[data-action="toggle"]` + `data-sample-id`.
   - Carousel "Adicione mais amostras" (snippets/rk-sample-pick.liquid), cujo
     botão ADICIONAR/ADICIONADO usa o mesmo contrato `[data-action="toggle"]`
     + `data-sample-id`, alternando a classe `.is-selected` no card.
   - Rodapé de valores (linha por amostra + total) e botão "Adicionar ao
     carrinho", que envia todas as amostras selecionadas em lote para
     /cart/add.js.

   Um único handler de toggle cobre ambos os controles porque ambos
   representam a mesma ação: alternar se a amostra `data-sample-id` está
   selecionada.
   ========================================================================== */

import { RkComponent } from '@rook/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

class RkSampleDrawerElement extends RkComponent {
    static hydration = 'idle';

    setup() {
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
        this.currency = this.dataset.currency || 'BRL';
        this.isSubmitting = false;

        this.delegate({
            'click [data-action="toggle"]': this.#onToggle,
            'click [data-action="add-all"]': this.#onAddAll,
        });

        this.#syncState();
    }

    #onToggle(event, target) {
        const id = target.dataset.sampleId;
        if (!id) return;

        const pick = this.querySelector(`.rk-sample-pick[data-sample-id="${id}"]`);
        if (!pick) return;

        const isSelected = pick.classList.toggle('is-selected');
        pick.querySelector('.rk-sample-pick__toggle')?.setAttribute('aria-pressed', String(isSelected));

        this.#hideSubmitError();
        this.#syncState();
    }

    #syncState() {
        const { empty, listCards = [], lineItems = [], total, submit } = this.refs;
        const selectedPicks = this.querySelectorAll('.rk-sample-pick.is-selected');
        const selectedIds = new Set(Array.from(selectedPicks, (pick) => pick.dataset.sampleId));

        let cents = 0;
        selectedPicks.forEach((pick) => {
            cents += Number(pick.dataset.price) || 0;
        });

        listCards.forEach((card) => {
            card.hidden = !selectedIds.has(card.dataset.sampleId);
        });

        lineItems.forEach((line) => {
            line.hidden = !selectedIds.has(line.dataset.sampleLine);
        });

        if (empty) empty.hidden = selectedIds.size > 0;
        if (total) total.textContent = formatMoney(cents, this.moneyFormat, this.currency);
        if (submit) submit.disabled = this.isSubmitting || selectedIds.size === 0;
    }

    #onAddAll() {
        if (this.isSubmitting) return;

        const items = Array.from(this.querySelectorAll('.rk-sample-pick.is-selected'), (pick) => ({
            id: Number(pick.dataset.variantId),
            quantity: 1,
        })).filter((item) => item.id);

        if (items.length === 0) return;

        this.isSubmitting = true;
        this.#hideSubmitError();
        const { submit } = this.refs;
        submit?.classList.add('is-loading');
        if (submit) submit.disabled = true;

        fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify({ items }) }))
            .then((response) => response.json())
            .then((response) => {
                if (response.status) {
                    this.dispatchEvent(
                        new CartErrorEvent(this.id, response.message, response.description, response.errors)
                    );
                    this.#showSubmitError(response.message);
                    return;
                }

                this.dispatchEvent(
                    new CartAddEvent(response, this.id, {
                        source: 'product-form-component',
                        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
                    })
                );

                this.#resetSelection();
                this.querySelector('rk-drawer-element')?.close?.();
            })
            .catch((error) => console.error(error))
            .finally(() => {
                this.isSubmitting = false;
                submit?.classList.remove('is-loading');
                this.#syncState();
            });
    }

    #resetSelection() {
        this.querySelectorAll('.rk-sample-pick.is-selected').forEach((pick) => {
            pick.classList.remove('is-selected');
            pick.querySelector('.rk-sample-pick__toggle')?.setAttribute('aria-pressed', 'false');
        });
    }

    #showSubmitError(message) {
        const { submitError } = this.refs;
        if (!submitError || !message) return;
        submitError.textContent = message;
        submitError.hidden = false;
    }

    #hideSubmitError() {
        const { submitError } = this.refs;
        if (!submitError) return;
        submitError.hidden = true;
    }
}

if (!customElements.get('rk-sample-drawer-element')) {
    customElements.define('rk-sample-drawer-element', RkSampleDrawerElement);
}
